import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Spinner } from '@/components/ui'
import { Separator } from '@/components/ui/separator.tsx'
import { useDeliverableSetById } from '@/features/deliverable-set'
import { useDocumentStore } from '@/features/document'
import { useDocumentList } from '@/features/document/hooks'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { DocumentStatus, ResponseDocumentListItem } from '@/schema'
import { cn } from '@/utils'
import { DEFAULT_GET_ALL_LIMIT } from '@/utils/constants'
import {
  buildDeliverableSetDocument,
  buildDeliverableSetRoute,
  buildFileUploadFromDocumentRoute,
} from '@/utils/routes'

import { useTFLSidebarDocuments, useTFLSidebarSearch } from '../hooks'
import { useCommentStore, useTflViewerStore } from '../store'

import { TFLSidebarDeleteFooter } from './TFLSidebar/TFLSidebarDeleteFooter'
import { TFLSidebarDeleteHeader } from './TFLSidebar/TFLSidebarDeleteHeader'
import { TFLSidebarFileList } from './TFLSidebar/TFLSidebarFileList'
import { TFLSidebarHeader } from './TFLSidebar/TFLSidebarHeader'
import { TFLSidebarSearchAndFilter } from './TFLSidebar/TFLSidebarSearchAndFilter'
import { TFLSidebarTOCButton } from './TFLSidebar/TFLSidebarTOCButton'
import { TFLFilterTabs } from './TFLFilterTabs'
import { TFLSearchResult } from './TFLSearchResult'

export const TFLSidebar = () => {
  const { id: deliverableSetId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const {
    filters,
    isDeleteMultiple,
    deleteMultipleDocumentIds,
    openDocuments,
    activeDocumentId,
    setFilter,
    setIsDeleteMultiple,
    setDeleteMultipleDocumentIds,
    setDeleteDialogOpen,
    setOpenDocument,
    setActiveDocumentId,
    isLoadingTOC,
    setLoadingTOC,
    tocError,
    setTOCError,
    showBookmarked,
  } = useDocumentStore()

  const { isSidebarCollapsed, setSidebarCollapsed } = useTflViewerStore()

  const { reset: resetCommentStore } = useCommentStore()

  const [searchText, setSearchText] = useState('')

  const isOnTOCPage = useMemo(
    () => location.pathname === `/deliverable-set/${deliverableSetId}/document`,
    [location.pathname, deliverableSetId]
  )

  const { isLg: isLargeBreakpoint } = useBreakpoint()

  const handleNavigateToTOC = () => {
    if (!deliverableSetId) return
    navigate(buildDeliverableSetDocument(deliverableSetId))
  }

  const {
    results,
    loadMoreRef,
    isLoading: isLoadingSearch,
    hasNextPage,
    searchQuery,
  } = useTFLSidebarSearch({
    searchText,
  })

  const {
    data: { data = [] } = {},
    isError,
    isLoading,
    error,
    refetch,
  } = useDocumentList({
    deliverableSetId: deliverableSetId || '',
    search: filters.searchName,
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    filters,
  })

  const isFiltered = useMemo(() => {
    return (
      filters.searchName !== '' ||
      filters.statuses?.length > 0 ||
      filters.type !== 'All' ||
      showBookmarked
    )
  }, [filters, showBookmarked])

  const { sortedData } = useTFLSidebarDocuments(data, deliverableSetId)

  // Filter bookmarked files if enabled
  const filteredData = useMemo(() => {
    if (showBookmarked) {
      return sortedData.filter((doc) => doc.is_bookmarked === true)
    }
    return sortedData
  }, [sortedData, showBookmarked])

  // Recalculate isSelectAll based on filtered data
  const isSelectAll = useMemo(
    () =>
      filteredData.length > 0 &&
      deleteMultipleDocumentIds.length === filteredData.length,
    [deleteMultipleDocumentIds, filteredData]
  )

  const { data: deliverableSet } = useDeliverableSetById(deliverableSetId)

  useEffect(() => {
    if (!isLoading && data.length === 0 && !!deliverableSetId && !isFiltered) {
      navigate(buildDeliverableSetRoute(deliverableSetId || ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading])

  useEffect(() => {
    setLoadingTOC(isLoading)
  }, [isLoading, setLoadingTOC])

  useEffect(() => {
    setTOCError(isError ? error : null)
  }, [isError, error, setTOCError])

  const handleFileClick = (item: ResponseDocumentListItem) => {
    if (!deliverableSetId) return
    const isOpening = openDocuments.some((doc) => doc.id === item.document_id)
    if (!isOpening) {
      setOpenDocument([
        ...openDocuments,
        { id: item.document_id, filename: item.display_name },
      ])
    }
    setActiveDocumentId(item.document_id)
  }

  const handleDeleteSingle = (item: ResponseDocumentListItem) => {
    setDeleteDialogOpen(true)
    setDeleteMultipleDocumentIds([item.document_id])
  }

  const handleDeleteMulti = (item: ResponseDocumentListItem) => {
    if (!isDeleteMultiple) setIsDeleteMultiple(true)
    setDeleteMultipleDocumentIds([
      ...deleteMultipleDocumentIds,
      item.document_id,
    ])
  }

  const handleSelectDelete = (item: ResponseDocumentListItem) => {
    setDeleteMultipleDocumentIds([
      ...deleteMultipleDocumentIds,
      item.document_id,
    ])
  }

  const handleDeselectDelete = (item: ResponseDocumentListItem) => {
    setDeleteMultipleDocumentIds(
      deleteMultipleDocumentIds.filter((id) => id !== item.document_id)
    )
  }

  const handleSelectStatus = (status: DocumentStatus) => {
    setFilter({
      ...filters,
      statuses: [...filters.statuses, status],
    })
  }

  const handleDeselectStatus = (status: DocumentStatus) => {
    setFilter({
      ...filters,
      statuses: filters.statuses.filter((s) => s !== status),
    })
  }

  const handleSelectAllChange = () => {
    if (isSelectAll) {
      setDeleteMultipleDocumentIds([])
    } else {
      setDeleteMultipleDocumentIds(filteredData.map((item) => item.document_id))
    }
  }

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(true)
  }

  const handleUpload = () => {
    if (!deliverableSetId) return
    const currentPath = globalThis.location.pathname
    navigate(buildFileUploadFromDocumentRoute(deliverableSetId), {
      state: { from: currentPath },
    })
  }

  const handleBackClick = () => {
    resetCommentStore()
  }

  if (!deliverableSetId) {
    return (
      <div className="p-6 text-sm text-gray-500" role="alert">
        Missing deliverable set id.
      </div>
    )
  }
  return (
    <div
      data-sidebar
      className={cn(
        'bg-white border-r border-r-primary',
        'relative h-full flex flex-col bg-white transition-all duration-300 overflow-hidden',
        isSidebarCollapsed ? 'w-0' : 'w-sidebar',
        isLargeBreakpoint ? '' : 'absolute top-0 left-0 bottom-0'
      )}
    >
      <div className="w-sidebar h-full flex flex-col">
        <div
          className={cn(
            'flex flex-col gap-xl py-4 px-3',
            isSidebarCollapsed ? 'px-2' : ''
          )}
        >
          <TFLSidebarHeader
            deliverableSetId={deliverableSetId}
            isSidebarCollapsed={isSidebarCollapsed}
            onBackClick={handleBackClick}
            onUpload={handleUpload}
            onToggleCollapse={() => setSidebarCollapsed(!isSidebarCollapsed)}
          />

          {!isSidebarCollapsed && (
            <TFLFilterTabs
              activeTab={filters.type}
              onTabChange={(value) => setFilter({ ...filters, type: value })}
            />
          )}
        </div>

        <Separator />

        {!isSidebarCollapsed && (
          <TFLSidebarSearchAndFilter
            searchText={searchText}
            onSearchChange={setSearchText}
            onSelectStatus={handleSelectStatus}
            onDeselectStatus={handleDeselectStatus}
            files={sortedData}
          />
        )}

        {!isSidebarCollapsed && (
          <div className="sticky top-0 z-10 bg-white">
            {(() => {
              // Loading search results
              if (isLoadingSearch) {
                return (
                  <div className="flex items-center justify-center p-8 flex-1">
                    <Spinner />
                  </div>
                )
              }

              // Search query for fulltext search
              if (searchQuery) return null

              // Delete multiple documents
              if (isDeleteMultiple) {
                return (
                  <TFLSidebarDeleteHeader
                    selectedCount={deleteMultipleDocumentIds.length}
                    totalCount={filteredData.length}
                    isSelectAll={isSelectAll}
                    onSelectAllChange={handleSelectAllChange}
                  />
                )
              }

              // TOC button
              return (
                <TFLSidebarTOCButton
                  isOnTOCPage={isOnTOCPage}
                  isActive={activeDocumentId === null}
                  onClick={() => {
                    if (isOnTOCPage) {
                      setActiveDocumentId(null)
                    } else {
                      handleNavigateToTOC()
                    }
                  }}
                  onKeyDown={
                    isOnTOCPage
                      ? undefined
                      : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleNavigateToTOC()
                          }
                        }
                  }
                />
              )
            })()}
          </div>
        )}

        {!isSidebarCollapsed && (
          <div className="flex-1 overflow-y-auto">
            {(() => {
              // Search query for fulltext search
              if (searchQuery) {
                return (
                  <TFLSearchResult
                    results={results}
                    loadMoreRef={loadMoreRef}
                    isLoading={isLoadingSearch}
                    hasNextPage={hasNextPage}
                  />
                )
              }

              // File list
              return (
                <TFLSidebarFileList
                  isLoading={isLoadingTOC}
                  error={tocError}
                  onRefetch={refetch}
                  sortedData={filteredData}
                  activeDocumentId={activeDocumentId}
                  onFileClick={handleFileClick}
                  onDeleteSingle={handleDeleteSingle}
                  onDeleteMulti={handleDeleteMulti}
                  onSelectDelete={handleSelectDelete}
                  onDeselectDelete={handleDeselectDelete}
                  deliverableSet={deliverableSet}
                />
              )
            })()}
          </div>
        )}
        {isDeleteMultiple && (
          <TFLSidebarDeleteFooter
            selectedCount={deleteMultipleDocumentIds.length}
            onCancel={() => {
              setDeleteMultipleDocumentIds([])
              setIsDeleteMultiple(false)
            }}
            onConfirm={handleConfirmDelete}
          />
        )}
      </div>
    </div>
  )
}
